import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building, CheckCircle2, Lock, Mail, User } from 'lucide-react';
import SpaceJetNavbar from '../components/SpaceJetNavbar';
import SpaceJetParticleField from '../components/SpaceJetParticleField';
import { ThemeProvider } from '../contexts/ThemeContext';
import { authAPI, overlapAPI } from '../api';

const DEFAULT_CATEGORY_OPTIONS = [
  'Travel', 'Finance', 'E-commerce', 'SaaS', 'Health', 'Gaming', 'Lead Generation', 'Mobile Apps', 'Education', 'Beauty',
  'Fashion', 'Insurance', 'Real Estate', 'Automotive', 'B2B Services', 'Crypto', 'Home Services', 'Food & Beverage',
  'Finance Apps', 'Subscription Boxes', 'Software Reviews', 'Personal Finance', 'E-learning', 'Lifestyle', 'Pet Care',
];

const SpaceJetNetworkSignup = () => {
  const navigate = useNavigate();
  const [accountType, setAccountType] = useState('affiliate');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    company: '',
    website: '',
    overlapNotes: '',
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORY_OPTIONS);
  const [categorySearch, setCategorySearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await overlapAPI.categories();
        const rows = response?.data?.categories || [];
        const names = rows.map((row) => row.name).filter(Boolean);
        if (names.length > 0) setCategoryOptions(names);
      } catch (fetchError) {
        console.error('Failed to load overlap categories:', fetchError);
      }
    };

    fetchCategories();
  }, []);

  const questionLabel = accountType === 'advertiser' ? 'What traffic categories are you looking for?' : 'What offer categories are you looking for?';
  const helperLabel = accountType === 'advertiser' ? 'Select the traffic categories that best match your campaign goals.' : 'Select the offer categories you want to promote.';

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => (prev.includes(category) ? prev.filter((item) => item !== category) : [...prev, category]));
  };

  const visibleCategoryOptions = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) return categoryOptions;
    return categoryOptions.filter((category) => category.toLowerCase().includes(query));
  }, [categoryOptions, categorySearch]);

  const selectAllVisibleCategories = () => {
    setSelectedCategories((prev) => {
      const next = [...prev];
      visibleCategoryOptions.forEach((category) => {
        if (!next.includes(category)) next.push(category);
      });
      return next;
    });
  };

  const clearSelectedCategories = () => setSelectedCategories([]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (selectedCategories.length === 0) {
      setError('Please choose at least one category so we can match overlaps later.');
      return;
    }

    setSubmitting(true);

    const payload = {
      email: formData.email,
      password: formData.password,
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      type: accountType,
      company: formData.company || null,
      overlap_categories: selectedCategories,
      overlap_notes: formData.overlapNotes || null,
      source: 'spacejet-portal-signup',
      signup_details: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        website: formData.website || null,
        overlap_categories: selectedCategories,
        overlap_notes: formData.overlapNotes || null,
      },
    };

    try {
      await authAPI.register(payload);
    } catch (registerError) {
      setError(registerError?.message || 'Registration failed');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    navigate(`/pending-approval?type=${accountType}`);
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-[#050b17] text-slate-100 spacejet-texture-dots spacejet-texture-living transition-colors duration-300" style={{ '--spacejet-texture-dot-opacity': 0.105, '--spacejet-texture-grid-opacity': 0.07, '--spacejet-texture-ambient-opacity': 0.085, '--spacejet-texture-drift-duration': '50s', '--spacejet-texture-glow-duration': '35s', '--spacejet-texture-sheen-duration': '41s' }}>
        <SpaceJetNavbar />
        <SpaceJetParticleField maxParticles={42} speed={0.14} baseOpacity={0.2} />

        <section className="relative overflow-hidden py-12 md:py-16">
          <div className="absolute inset-0 spacejet-gradient-overlay" />
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="spacejet-page-kicker mb-4 justify-center">Apply</div>
            <h1 className="spacejet-page-title text-4xl font-semibold text-slate-100 md:text-5xl">Join SpaceJet Network</h1>
            <p className="spacejet-page-copy mx-auto mt-5 max-w-2xl text-xl">
              Choose your sign-up type and tell us your target categories so we can surface partner overlap.
            </p>
          </div>
        </section>

        <section className="pb-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="spacejet-surface-card rounded-2xl p-8">
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                <button type="button" onClick={() => setAccountType('affiliate')} className={`rounded-xl border p-4 text-left transition-colors ${accountType === 'affiliate' ? 'spacejet-selected' : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'}`}>
                  <div className="mb-1 text-slate-100 font-semibold">Publisher sign-up</div>
                  <div className="text-sm text-slate-400">Saves your application for SpaceJet review.</div>
                </button>
                <button type="button" onClick={() => setAccountType('advertiser')} className={`rounded-xl border p-4 text-left transition-colors ${accountType === 'advertiser' ? 'spacejet-selected' : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'}`}>
                  <div className="mb-1 text-slate-100 font-semibold">Advertiser sign-up</div>
                  <div className="text-sm text-slate-400">Saves your advertiser interest for SpaceJet review.</div>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <div className="spacejet-page-kicker mb-5">Account details</div>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-300">First name *</span>
                      <div className="relative">
                        <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input type="text" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 pl-10 pr-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Jane" />
                      </div>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-300">Last name *</span>
                      <input type="text" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Smith" />
                    </label>

                    <label className="block md:col-span-2">
                      <span className="mb-2 block text-sm font-medium text-slate-300">Email address *</span>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input type="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 pl-10 pr-4 py-3 text-slate-100 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="you@company.com" />
                      </div>
                    </label>

                    <label className="block md:col-span-2">
                      <span className="mb-2 block text-sm font-medium text-slate-300">Password *</span>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input type="password" name="password" required minLength={6} value={formData.password} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 pl-10 pr-4 py-3 text-slate-100 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="At least 6 characters" />
                      </div>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-300">Company</span>
                      <div className="relative">
                        <Building className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input type="text" name="company" value={formData.company} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 pl-10 pr-4 py-3 text-slate-100 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Company name" />
                      </div>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-300">Website</span>
                      <input type="url" name="website" value={formData.website} onChange={handleInputChange} className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="https://example.com" />
                    </label>
                  </div>
                </div>

                <div>
                  <div className="spacejet-page-kicker mb-2">{questionLabel}</div>
                  <p className="mb-4 text-sm text-slate-400">{helperLabel}</p>

                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <input type="text" value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder="Search categories" />
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <span className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2">Selected: {selectedCategories.length}</span>
                      <button type="button" onClick={selectAllVisibleCategories} className="rounded-lg border border-slate-700 px-3 py-2 text-slate-300 transition-colors hover:border-slate-600 hover:text-slate-100">Select all</button>
                      <button type="button" onClick={clearSelectedCategories} className="rounded-lg border border-slate-700 px-3 py-2 text-slate-300 transition-colors hover:border-slate-600 hover:text-slate-100">Clear</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                    {visibleCategoryOptions.map((category) => {
                      const checked = selectedCategories.includes(category);
                      return (
                        <button key={category} type="button" onClick={() => toggleCategory(category)} className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${checked ? 'spacejet-selected spacejet-accent-text' : 'border-slate-700 bg-slate-800/30 text-slate-300 hover:border-slate-600'}`}>
                          <span className="inline-flex items-center">
                            {checked && <CheckCircle2 className="mr-2 h-4 w-4" />}
                            {category}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {visibleCategoryOptions.length === 0 && <p className="mt-4 text-sm text-slate-400">No categories match your search.</p>}
                </div>

                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-300">{accountType === 'advertiser' ? 'Traffic notes' : 'Offer notes'}</span>
                  <textarea name="overlapNotes" value={formData.overlapNotes} onChange={handleInputChange} rows={4} className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-slate-100 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder={accountType === 'advertiser' ? 'Tell us what traffic quality, GEOs, devices, or compliance constraints you need.' : 'Tell us what offers, GEOs, payout models, or verticals you want access to.'} />
                </label>

                {error && <div className="rounded-lg border border-red-700/60 bg-red-900/40 px-4 py-3 text-sm text-red-200">{error}</div>}

                <div className="pt-2">
                  <button type="submit" disabled={submitting} className="spacejet-btn-primary group inline-flex w-full items-center justify-center rounded-lg px-8 py-4 font-semibold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60">
                    {submitting ? 'Submitting...' : accountType === 'affiliate' ? 'Continue publisher application' : 'Submit advertiser interest'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </ThemeProvider>
  );
};

export default SpaceJetNetworkSignup;
